import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SEOHead from './SEOHead';

const {
  FiArrowLeft, FiArrowRight, FiShield, FiAlertTriangle, FiEye, FiUserCheck, FiBookOpen, FiBarChart2,
  FiCpu, FiCopy, FiEdit3,
} = FiIcons;

const checks = [
  { icon: FiCpu, t: 'Likelihood signals (per-sentence view coming soon)', d: 'See where your manuscript reads as probably-human, mixed, or probably-AI. Today the insight is manuscript-level; the sentence-by-sentence highlighter is rolling out next. A flag to guide your review, not a verdict.' },
  { icon: FiCopy, t: 'Protect your originality', d: 'Flag accidental overlap and trace close matches against your own library and our database - so you can rewrite before you publish.' },
  { icon: FiBarChart2, t: 'Writing analysis', d: 'Readability, vocabulary richness, sentence variety, and flow - at a glance.' },
  { icon: FiEdit3, t: 'Rewrite in your voice', d: 'When a line reads generic, replace it with your actual Voice DNA - so the words are genuinely yours.' },
  { icon: FiShield, t: 'Read in context of you', d: 'Likelihood signals are shown alongside your Voice DNA - so plain or non-native writing is understood, not unfairly flagged.' },
  { icon: FiEye, t: 'Understand machine-readability', d: 'See how your prose looks to statistical models, with confidence and uncertainty made explicit - never a bare accusation.' },
];

// per-sentence likelihood demo — a flag to guide review, not a verdict
const passage = [
  { t: 'Theo arrived the way weather does — without asking, and impossible to ignore.', c: 98 },
  { t: 'She had spent the morning rehearsing what she would say.', c: 96 },
  { t: 'The subject demonstrates significant emotional resonance and thematic depth.', c: 41 },
  { t: 'And now, watching him shake the rain from his coat in her doorway, every careful sentence left her at once.', c: 97 },
];
const hue = (c) => (c >= 75 ? 'bg-emerald-100 text-emerald-900' : c >= 55 ? 'bg-amber-100 text-amber-900' : 'bg-rose-100 text-rose-900');

const metrics = [
  ['Reads as human (est.)', '96%'], ['AI-likelihood (est.)', '4%'], ['Matches your voice', '95%'],
  ['Readability', 'Grade 7 · clear'], ['Vocabulary richness', 'High'], ['Sentence variety', 'Strong'],
];
const why = [
  { icon: FiBookOpen, t: 'KDP & Amazon AI disclosure', d: 'Publishing platforms increasingly ask whether AI was involved. Understand how your prose reads before you answer - in your own words.' },
  { icon: FiUserCheck, t: 'Reader & reviewer trust', d: 'Readers are quick to cry "AI." Knowing where your prose drifts generic lets you tighten it into your own voice first.' },
  { icon: FiShield, t: 'Your originality', d: 'Your name is on the cover. Catch accidental overlap and generic lines early, so the book is unmistakably yours.' },
];

const AuthenticityPage = () => {
  const structuredData = {
    '@context': 'https://schema.org', '@type': 'WebPage',
    name: 'Authenticity Insight — ManuscriptSmith',
    description: 'Understand how your prose reads to machines and protect your originality, with likelihood signals and Voice-DNA matching. An estimate to guide review, not proof.',
    url: 'https://manuscriptsmith.com/authenticity',
  };
  return (
    <div className="min-h-screen bg-gradient-to-br from-parchment-50 to-parchment-100">
      <SEOHead
        title="Authenticity Insight — Protect Your Originality | ManuscriptSmith"
        description="Understand how your prose reads to statistical models and protect your originality. Likelihood signals guide your review, plus originality checks against your own library and our database. An estimate, not proof — never an accusation."
        keywords="authenticity insight for authors, protect writing originality, kdp ai disclosure, machine readability of writing, manuscript originality check"
        canonicalUrl="https://manuscriptsmith.com/authenticity"
        structuredData={structuredData}
      />

      <main>
        {/* Hero */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-10 text-center">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-secondary-700 bg-secondary-50 px-3 py-1 rounded-full mb-4">
            <SafeIcon icon={FiShield} className="w-4 h-4" /> Authenticity Insight
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Protect your <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-secondary-500">originality.</span> Understand how it reads.
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            This isn't a tool to beat AI detectors - it can't, and neither can they. It's an insight layer:
            see where your prose drifts generic, how it might read to machines, and where it overlaps your own library -
            so you can tighten it into unmistakably <em>your</em> voice before you publish.
          </p>
        </section>

        {/* Scorecard */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-10">
          <div className="grid sm:grid-cols-3 gap-4 mb-4">
            <div className="rounded-2xl border border-emerald-200 bg-white p-6 text-center shadow-sm"><div className="text-4xl font-bold text-emerald-600">96%</div><div className="text-sm font-semibold text-gray-700 mt-1">Reads as human</div><div className="text-xs text-gray-400">estimate, not proof</div></div>
            <div className="rounded-2xl border border-gray-200 bg-white p-6 text-center shadow-sm"><div className="text-4xl font-bold text-gray-700">4%</div><div className="text-sm font-semibold text-gray-700 mt-1">AI-likelihood</div><div className="text-xs text-gray-400">below the review threshold</div></div>
            <div className="rounded-2xl border border-primary-200 bg-white p-6 text-center shadow-sm"><div className="text-4xl font-bold text-primary-600">95%</div><div className="text-sm font-semibold text-gray-700 mt-1">Matches your voice</div><div className="text-xs text-gray-400">your Voice DNA</div></div>
          </div>
          <div className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-4 py-3">
            <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 flex-shrink-0 mt-0.5" /><span><strong>An estimate, not proof.</strong> These are likelihood signals to guide your own review. AI detectors have high false-positive rates - especially on plain, technical, or non-native-English prose - and are easily fooled by rewriting. Never use this to accuse anyone.</span>
          </div>
        </section>

        {/* AI highlighter demo */}
        <section className="bg-white py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <SafeIcon icon={FiEye} className="w-8 h-8 text-primary-500 mx-auto mb-3" />
              <h2 className="text-3xl font-bold text-gray-900 mb-2">See it sentence by sentence <span className="align-middle ml-2 text-xs font-bold uppercase tracking-wide text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full">Coming soon</span></h2>
              <p className="text-lg text-gray-600">Preview of the per-sentence highlighter now rolling out in the studio: color-coded <em>likelihood</em> signals show which lines read as you, and which drift toward generic, machine-like phrasing. A flag to guide your review, not a verdict. Manuscript-level authenticity insight is live today.</p>
            </div>
            <div className="bg-gray-50 rounded-2xl border border-gray-200 p-6">
              <p className="text-lg leading-9" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>
                {passage.map((s, i) => (
                  <span key={i} className={`rounded px-1 ${hue(s.c)}`} title={`${s.c}% human`}>{s.t} </span>
                ))}
              </p>
              <div className="mt-5 flex flex-wrap gap-4 text-xs text-gray-500">
                <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-emerald-200" /> Probably human (75%+)</span>
                <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-amber-200" /> Mixed (55–74%)</span>
                <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-rose-200" /> Probably generic / AI-like (&lt;55%)</span>
              </div>
              <div className="mt-3 inline-flex items-center gap-2 text-sm text-amber-700">
                <SafeIcon icon={FiAlertTriangle} className="w-4 h-4" /> 1 sentence worth a look — rewrite it in your voice with one tap.
              </div>
              <p className="mt-3 text-xs text-gray-400">Percentages are estimates from statistical models, not proof. Short sentences are the least reliable - treat every flag as a prompt to reread, never as a verdict.</p>
            </div>
          </div>
        </section>

        {/* Metrics */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="text-center mb-8">
            <SafeIcon icon={FiBarChart2} className="w-8 h-8 text-primary-500 mx-auto mb-3" />
            <h2 className="text-3xl font-bold text-gray-900">A full writing-quality readout</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {metrics.map(([k, v]) => (
              <div key={k} className="bg-white rounded-xl border border-gray-200 px-4 py-4 text-center">
                <div className="text-lg font-bold text-gray-900">{v}</div>
                <div className="text-xs text-gray-500 mt-0.5">{k}</div>
              </div>
            ))}
          </div>
        </section>

        {/* What the engine checks */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">One engine, every check</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Built on our own analyzer - the same one that reads every book. All of it runs right where you write.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {checks.map((c, i) => (
              <motion.div key={c.t} className="flex items-start gap-4 p-5 rounded-2xl border border-gray-200 bg-white"
                initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: (i % 3) * 0.06 }}>
                <div className="w-11 h-11 rounded-xl bg-gradient-to-r from-primary-500 to-secondary-500 flex items-center justify-center flex-shrink-0">
                  <SafeIcon icon={c.icon} className="w-5 h-5 text-white" />
                </div>
                <div><h3 className="font-bold text-gray-900 mb-1">{c.t}</h3><p className="text-sm text-gray-600 leading-relaxed">{c.d}</p></div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Why it matters */}
        <section className="bg-white py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-10">Why authors care about this now</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {why.map((w) => (
                <div key={w.t} className="p-6 rounded-2xl border border-gray-200 bg-gradient-to-br from-parchment-50 to-white">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-primary-500 to-secondary-500 flex items-center justify-center mb-4"><SafeIcon icon={w.icon} className="w-6 h-6 text-white" /></div>
                  <h3 className="font-bold text-gray-900 mb-2">{w.t}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{w.d}</p>
                </div>
              ))}
            </div>
            <div className="mt-8 max-w-3xl mx-auto bg-primary-50 border border-primary-100 rounded-2xl p-5 text-center">
              <p className="text-sm text-primary-900">
                <strong>Why we show context, not verdicts:</strong> generic detectors falsely flag plain, non-native, or
                unusual writing - and no tool can reliably catch reworded AI or guarantee any detector result. So we
                don't pretend to. Because ManuscriptSmith knows <em>your</em> Voice DNA, it shows likelihood signals
                <em> alongside</em> how you actually write - to help you tighten your own prose, not to hand you a
                label you could use against someone.
              </p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl p-10 text-center text-white shadow-xl">
            <h2 className="text-3xl font-bold mb-4">Publish with confidence it's yours</h2>
            <p className="text-lg opacity-90 mb-8">Write in your voice and protect your originality, chapter by chapter.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/signup" className="inline-flex items-center justify-center px-8 py-4 bg-white text-primary-600 font-bold text-lg rounded-lg hover:bg-gray-50">
                Get started <SafeIcon icon={FiArrowRight} className="w-5 h-5 ml-2" />
              </Link>
              <Link to="/analysis" className="inline-flex items-center justify-center px-8 py-4 bg-white/15 border border-white/40 text-white font-semibold text-lg rounded-lg hover:bg-white/25">
                See a full analysis
              </Link>
            </div>
          </div>
          <p className="mt-6 max-w-3xl mx-auto text-center text-xs text-gray-500 leading-relaxed">
            <strong className="text-gray-600">Honest by design:</strong> Authenticity Insight is an estimate, not proof.
            AI detectors have high false-positive rates - especially on plain, technical, or non-native-English writing -
            and no tool, ours included, can reliably detect reworded AI or guarantee how any third-party detector will score your text.
            Use these signals to improve your own writing. Never use them to accuse anyone.
          </p>
        </section>
      </main>
    </div>
  );
};

export default AuthenticityPage;
